import history from 'browserHistory'
import RoutesHelper, { getAbsolutePath } from 'helpers/RoutesHelper'
import {
  SIGNIN_START,
  SIGNIN_SUCCESS,
  SIGNIN_ERROR,
  REQUEST_RESET_PASSWORD_START,
  REQUEST_RESET_PASSWORD_SUCCESS,
  REQUEST_RESET_PASSWORD_ERROR,
  RESET_PASSWORD_START,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_ERROR,
  TOKEN_RENEWAL,
} from './actionTypes'

import { logger } from 'logger'

import AuthAPI from 'remote/auth'

import * as AuthSelectors from 'data/auth/selectors'
import * as ConfigSelectors from 'data/config/selectors'
import * as OrganizationSelectors from 'data/organizations/selectors'

import { storeTokens, clearTokens } from '../tokenStore'
import { start } from 'data/app/actions'
import {
  createSuccessToasterMessage,
  createErrorToasterMessage,
} from 'data/ui/actions'
import { dataFetched } from 'data/graphql/reducer'
import {
  saveOrganizationAsJoined,
  removeOrganizationFromJoined,
} from 'helpers/OrganizationsHelpers'

export const ON_USER_FETCH_SUCCESS = 'ON_USER_FETCH_SUCCESS'
export const ON_USER_FETCH_ERROR = 'ON_USER_FETCH_ERROR'
export const SIGN_OUT = 'SIGN_OUT'

function isReturnToParameterValid(appDomain, returnTo) {
  try {
    const parts = new URL(returnTo)

    return parts.host.endsWith(appDomain)
  } catch (error) {
    // * URL constructor throws if URL is not absolute and second parameter is not specified.
    return true
  }
}

const isReturnToDownloadLink = returnTo =>
  new RegExp(/api\/backup-organization\/.*/).test(returnTo)

function handleReturnTo(returnTo) {
  if (returnTo.startsWith('http')) {
    window.location.href = returnTo
    return
  }

  window.location.href = getAbsolutePath(returnTo)
}

export function signOut({ removeTokens = true, redirectTo = null } = {}) {
  return function({ getState }) {
    const state = getState()
    const apiEndpoint = ConfigSelectors.getApiEndpoint(state)
    const apiToken = AuthSelectors.getApiToken(state)
    const organizationId = OrganizationSelectors.getOrganizationId(state)
    const cookieDomain = ConfigSelectors.getCookieDomain(state)

    function callback() {
      removeOrganizationFromJoined(cookieDomain, organizationId)
      clearTokens()
      redirectTo ? (window.location = redirectTo) : window.location.reload()
    }

    if (removeTokens) {
      return {
        type: SIGN_OUT,
        payload: {
          promise: AuthAPI.signOut({ apiEndpoint, apiToken })
            .then(callback)
            .catch(callback),
        },
      }
    }
    redirectTo ? (window.location = redirectTo) : window.location.reload()
    return { type: SIGN_OUT }
  }
}

export function periodicRenewToken({ tokens }) {
  return ({ dispatch, getState }) => {
    const state = getState()
    const cookieDomain = ConfigSelectors.getCookieDomain(state)
    const apiEndpoint = ConfigSelectors.getApiEndpoint(state)
    const organizationId = OrganizationSelectors.getOrganizationId(state)

    const promise = renewTokens({
      tokens,
      organizationId,
      apiEndpoint,
      cookieDomain,
    })
      .then(tokens => ({ tokens }))
      .catch(error => {
        const status = error.status || (error.response && error.response.status)

        if (status && status >= 400 && status < 500) {
          localStorage.removeItem('TOKENS') // clear existing tokens
          window.location = `/auth/login?error=${error.id || error.message}`
        } else {
          // silently fails
          logger.warn(`failed to renew tokens for network reasons`)
        }
      })

    return {
      type: TOKEN_RENEWAL,
      payload: {
        promise,
      },
    }
  }
}

export const renewTokens = async ({
  tokens,
  organizationId,
  cookieDomain,
  apiEndpoint,
}) => {
  // recover tokens
  const { renewalToken } = tokens
  if (!renewalToken) {
    throw new Error('User does not have renewal token')
  }

  // fetch new tokens
  const newTokens = await AuthAPI.renewTokens({ apiEndpoint }, { renewalToken })

  // store tokens and org
  storeTokens(newTokens)
  saveOrganizationAsJoined(cookieDomain, organizationId)

  return newTokens
}

export const signInSuccess = ({ tokens }) => () => {
  if (!tokens) {
    const error = new Error(
      'signInSuccess action creator called with empty tokens'
    )
    logger.warn(error.message)
    logger.warn(error.stack)
  }
  return {
    type: SIGNIN_SUCCESS,
    payload: {
      tokens,
    },
  }
}

export const signInFromTokens = ({ tokens, returnTo }) => ({
  dispatch,
  getState,
}) => {
  const state = getState()
  const appDomain = ConfigSelectors.getAppDomain(state)
  const cookieDomain = ConfigSelectors.getCookieDomain(state)
  const apiEndpoint = ConfigSelectors.getApiEndpoint(state)
  const organizationId = OrganizationSelectors.getOrganizationId(state)

  renewTokens({
    tokens,
    organizationId,
    apiEndpoint,
    cookieDomain,
  }).then(
    tokens => {
      if (tokens.organizationId !== organizationId) {
        localStorage.removeItem('TOKENS') // clear existing tokens
        window.location = `/auth/login?error=org-mismatch`
        return
      }
      logger.debug('Renew tokens', 'success')
      dispatch(signInSuccess({ tokens }))

      if (returnTo && isReturnToParameterValid(appDomain, returnTo)) {
        const absolutePath = getAbsolutePath(returnTo)
        if (isReturnToDownloadLink(absolutePath)) {
          window.location.href = absolutePath
        } else {
          return handleReturnTo(returnTo)
        }
      }

      history.replace(RoutesHelper.app())
      dispatch(start())
    },
    error => {
      if (error.status && error.status >= 400 && error.status < 500) {
        localStorage.removeItem('TOKENS') // clear existing tokens
        window.location = `/auth/login?error=${error.id}`
      } else {
        // silently fails
        dispatch({ type: SIGNIN_ERROR, payload: error })
        logger.warn(`failed to renew tokens for network reasons`)
      }
    }
  )

  return {
    type: SIGNIN_START,
    payload: {
      tokens,
      organizationId,
    },
  }
}

const fetchTokensFromEmailAndPassword = async (
  { email, password, organizationId },
  { getState }
) => {
  const state = getState()
  const apiEndpoint = ConfigSelectors.getApiEndpoint(state)
  const cookieDomain = ConfigSelectors.getCookieDomain(state)

  // test credentials
  const tokens = await AuthAPI.getTokens(
    { apiEndpoint },
    {
      email,
      password,
      organizationId,
    }
  )

  // store tokens and org
  storeTokens(tokens)
  saveOrganizationAsJoined(cookieDomain, organizationId)

  return tokens
}

export const signInFromEmailAndPassword = ({ email, password, returnTo }) => ({
  dispatch,
  getState,
}) => {
  const state = getState()
  const organization = OrganizationSelectors.getOrganization(state)
  const appDomain = ConfigSelectors.getAppDomain(state)

  fetchTokensFromEmailAndPassword(
    { email, password, organizationId: organization.id },
    { dispatch, getState }
  ).then(
    tokens => {
      dispatch(signInSuccess({ tokens }))

      if (returnTo && isReturnToParameterValid(appDomain, returnTo)) {
        const absolutePath = getAbsolutePath(returnTo)
        if (isReturnToDownloadLink(absolutePath)) {
          window.location.href = absolutePath
        } else {
          return handleReturnTo(returnTo)
        }
      }

      history.replace(RoutesHelper.app())
      dispatch(start())
    },
    error => dispatch({ type: SIGNIN_ERROR, payload: error })
  )

  return {
    type: SIGNIN_START,
    payload: {
      email,
      password,
      organizationId: organization.id,
    },
  }
}

export const requestResetPassword = ({ email }) => ({ dispatch, getState }) => {
  const state = getState()
  const organizationId = OrganizationSelectors.getOrganizationId(state)
  const apiEndpoint = ConfigSelectors.getApiEndpoint(state)
  AuthAPI.requestResetPassword({ apiEndpoint }, { email, organizationId }).then(
    () => {
      dispatch({
        type: REQUEST_RESET_PASSWORD_SUCCESS,
        payload: {
          id: 'auth/request-reset-password-success',
          message: 'Check your inbox, we sent you a reset password email',
        },
      })
    },
    error => {
      dispatch({
        type: REQUEST_RESET_PASSWORD_ERROR,
        payload: error,
      })
    }
  )
  return {
    type: REQUEST_RESET_PASSWORD_START,
    payload: {
      email,
      organizationId,
    },
  }
}

export const resetPassword = ({ resetPasswordToken, password }) => ({
  dispatch,
  getState,
}) => {
  const state = getState()
  const apiEndpoint = ConfigSelectors.getApiEndpoint(state)
  AuthAPI.resetPassword({ apiEndpoint }, { resetPasswordToken, password }).then(
    () => {
      dispatch({
        type: RESET_PASSWORD_SUCCESS,
        payload: {
          id: 'auth/reset-password-success',
          message: 'Your password has been updated',
        },
      })
      history.push(RoutesHelper.login())
    },
    error => {
      dispatch({
        type: RESET_PASSWORD_ERROR,
        payload: error,
      })
    }
  )
  return {
    type: RESET_PASSWORD_START,
    payload: {
      resetPasswordToken,
      password,
    },
  }
}

export const revokeOrganizationSSO = () => {
  return ({ getState, dispatch, graphQLClient }) => {
    return {
      type: 'REVOKE_ORGANIZATION_SSO',
      payload: graphQLClient
        .mutate({
          query: AuthAPI.revokeOrganizationSSO,
        })
        .then(({ data }) => {
          dispatch(dataFetched(data.revokeOrganizationSSO))
        }),
    }
  }
}

export const sendReconciliationSSOEmail = ({ userId }) => {
  const variables = { userId }
  return ({ getState, dispatch, graphQLClient }) => {
    return {
      type: 'SEND_RECONCILIATION_SSO_EMAIL',
      variables,
      payload: graphQLClient
        .mutate({
          query: AuthAPI.sendReconciliationSSOEmail,
          variables,
        })
        .then(() => {
          dispatch(
            createSuccessToasterMessage({
              title: 'Successfully sent SSO binding email',
            })
          )
        })
        .catch(() => {
          dispatch(
            createErrorToasterMessage({
              title: 'Error while sending SSO binding email',
            })
          )
        }),
    }
  }
}
